<script lang="ts" setup>
import RichTextField from '@/components/common/RichTextField.vue';
import CurrentLanguage from '@/components/CurrentLanguage.vue';
import {
  EventItem,
  repeatOptions,
  titleRules,
  descriptionRules,
  defaultEventItem,
} from '@/models/event-item.model';
import { useEvents, Event } from '@/services/events.composable';
import { Actions, Mutations, ShiftCMSState } from '@/store';
import { EventsActions, EventsState } from '@/stores/events.store';
import { NEW_ID } from '@/util/constants';
import { computed, onMounted, PropType, ref, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { format } from 'date-fns';
import CategoryField from '../categories/CategoryField.vue';
import AddressField from '../common/AddressField.vue';
import CategoriesField from '../categories/CategoriesField.vue';
import { impactAreaItems } from '@/models/initiative.model';
import { useI18n } from 'vue-i18n';

const store = useStore<ShiftCMSState & { ev: EventsState }>();
const { t } = useI18n();

const events = useEvents();

const props = defineProps({
  data: {
    type: Object as PropType<EventItem>,
  },
});

const valid = ref<boolean>(false);

const eventItem = ref<EventItem>({ ...defaultEventItem });
let currentValue = ref<EventItem>({ ...defaultEventItem });

const action = computed<string>(() =>
  eventItem.value.id === NEW_ID ? 'Add' : 'Save',
);

const save = () => saveEventItem(null, false);

const saveEventItem = async (e: unknown, close = true) => {
  let data: { status: boolean; data: EventItem | null } = {
    status: false,
    data: null,
  };
  if (valid.value) {
    if (eventItem.value.id === NEW_ID) {
      data = await store.dispatch(EventsActions.AddEventItem, eventItem.value);
    } else {
      data = await store.dispatch(
        EventsActions.UpdateEventItem,
        eventItem.value,
      );
    }

    Object.assign(eventItem.value, { ...data.data });
  }
  if (data.status) {
    events.emit(Event.Saved, true);
    store.dispatch(Actions.ShowNotification, {
      message: 'Saved EventItem',
      type: 'success',
    });

    if (close) {
      store.commit(Mutations.HideDrawer);
      store.dispatch(EventsActions.GetEventItems);
    }
    currentValue.value = { ...eventItem.value };
    store.commit(Mutations.SetDirtyState, false);
  } else {
    store.dispatch(Actions.ShowNotification, {
      message: 'Failed to save Learning Item',
      type: 'error',
    });
  }
};

const updateLanguage = async () => {
  if (eventItem.value.id === NEW_ID) {
    Object.assign(eventItem, { title: '' });
  } else {
    const item = await store.dispatch(
      EventsActions.GetEventItem,
      eventItem.value.id,
    );

    if (item) {
      Object.assign(eventItem.value, {
        ...item,
        description: item.description || '',
        startDate: new Date(item.startDate || new Date().toISOString()),
        endDate: new Date(item.endDate || new Date().toISOString()),
      });
      currentValue.value = { ...eventItem.value };
    }
  }
};

onBeforeUnmount(() => {
  events.off(Event.LanguageChanged, updateLanguage);
});

onMounted(() => {
  events.on(Event.LanguageChanged, updateLanguage);

  if (props.data) {
    Object.assign(eventItem.value, {
      ...props.data,
      description: props.data.description || '',
      startDate: new Date(props.data.startDate || new Date().toISOString()),
      endDate: new Date(props.data.endDate || new Date().toISOString()),
    });

    currentValue.value = { ...eventItem.value };
  } else {
    Object.assign(eventItem.value, { ...defaultEventItem });

    currentValue.value = { ...defaultEventItem };
  }
});

const removeLabel = async () => {
  if (
    eventItem.value &&
    eventItem.value.id !== NEW_ID &&
    confirm('Are you absolutely sure you want to delete this "Event Item"?')
  ) {
    store.dispatch(EventsActions.DeleteEventItem, eventItem.value.id);

    store.commit(Mutations.HideDrawer);
  }
};

const togglePublished = async (published: boolean) => {
  if (eventItem.value.id !== -1) {
    store.dispatch(EventsActions.PublishEventItem, {
      id: eventItem.value.id,
      published,
    });
  }
  eventItem.value.published = published;
};
const toggleDismissed = async (dismissed: boolean) => {
  if (eventItem.value.id !== -1) {
    store.dispatch(EventsActions.DismissEventItem, {
      id: eventItem.value.id,
      dismissed,
    });
  }
  eventItem.value.dismissed = dismissed;
};

function checkUrl(url: string) {
  window.open(url, '_new');
}

const formatDate = (v: string): string => {
  return format(new Date(v), 'dd-MM-yyyy HH:mm');
};

const startDate = computed(
  () => new Date(eventItem.value.startDate || new Date()),
);
const endDate = computed(() => new Date(eventItem.value.endDate || new Date()));

const startTime = computed(() =>
  format(new Date(eventItem.value.startDate || new Date()), 'HH:mm'),
);
const endTime = computed(() =>
  format(new Date(eventItem.value.endDate || new Date()), 'HH:mm'),
);

const updateStartTime = (v: string | null) => {
  const d = new Date(eventItem.value.startDate || new Date());
  eventItem.value.startDate = new Date(
    format(d, `yyyy-MM-dd ${v}`),
  ).toISOString();
};
const updateEndTime = (v: string | null) => {
  const d = new Date(eventItem.value.endDate || new Date());
  eventItem.value.endDate = new Date(
    format(d, `yyyy-MM-dd ${v}`),
  ).toISOString();
};

const updateStartDate = (v: Date) => {
  eventItem.value.startDate = new Date(
    format(new Date(v || new Date()), `yyyy-MM-dd ${startTime.value}`),
  ).toISOString();
};
const updateEndDate = (v: Date) => {
  eventItem.value.endDate = new Date(
    format(new Date(v || new Date()), `yyyy-MM-dd ${endTime.value}`),
  ).toISOString();
};

function getSimpleValue(i: EventItem) {
  return {
    title: i.title,
    description: i.description,
    url: i.url,
    imageUrl: i.imageUrl,
    startDate: i.startDate,
    endDate: i.endDate,
    categories: i.categories.map((c) => c.id),
    address: i.address,
    location: i.location,
  };
}

const isDirty = computed<boolean>(() => {
  const isDirty =
    JSON.stringify(getSimpleValue(eventItem.value)) !==
    JSON.stringify(getSimpleValue(currentValue.value));

  store.commit(Mutations.SetDirtyState, isDirty);

  return isDirty;
});

let translatedAreaItems = impactAreaItems.map((i) => ({
  title: t(`initiative.edit.area_${i.value}`),
  value: i.value,
}));
</script>
<template>
  <v-form v-model="valid" @submit.prevent="saveEventItem">
    <v-container>
      <h2>
        {{ $t(action) }} an event item <span v-if="isDirty">*</span>

        <CurrentLanguage />
      </h2>
      <v-row>
        <v-col cols="8">
          <v-text-field
            v-model="eventItem.title"
            :rules="titleRules"
            label="Title"
            hint="Not used on eventItem page. Use a top level header in the description"
            required
          ></v-text-field>
        </v-col>
        <v-col cols="2" class="pt-3">
          <v-btn
            class="mt-2"
            block
            :disabled="eventItem.id === NEW_ID"
            @click="() => togglePublished(!eventItem.published)"
            :color="!eventItem.published ? 'success' : 'warning'"
            >{{ eventItem.published ? 'Un-publish' : 'Publish' }}</v-btn
          >
        </v-col>
        <v-col cols="2" class="pt-3">
          <v-btn
            class="mt-2"
            block
            :disabled="eventItem.id === NEW_ID"
            @click="() => toggleDismissed(!eventItem.dismissed)"
            :color="!eventItem.dismissed ? 'warning' : 'error'"
            >{{ eventItem.dismissed ? 'Un-dismiss' : 'Dismiss' }}</v-btn
          >
        </v-col>
      </v-row>
      <CategoryField
        main
        disabled
        v-model="eventItem.mainCategory"
        label="Main Category"
      />
      <CategoriesField v-model="eventItem.categories" />
      <v-row>
        <v-col cols="9">
          <v-text-field
            v-model="eventItem.url"
            label="URL"
            required
          ></v-text-field>
        </v-col>
        <v-col class="pt-6">
          <v-btn
            block
            color="info"
            @click="checkUrl(eventItem.url)"
            :disabled="eventItem.url == ''"
            >Check</v-btn
          >
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="9">
          <v-text-field
            v-model="eventItem.imageUrl"
            label="Image URL"
            required
          ></v-text-field>
        </v-col>
        <v-col class="pt-6">
          <v-btn
            block
            color="info"
            @click="checkUrl(eventItem.imageUrl)"
            :disabled="eventItem.imageUrl == ''"
            >Check</v-btn
          >
        </v-col>
      </v-row>
      <v-text-field
        v-model="eventItem.location"
        label="Location"
        required
      ></v-text-field>
      <AddressField v-model="eventItem.address" />
      <v-select
        v-model="eventItem.impactArea"
        :items="translatedAreaItems"
        :label="$t('initiative.edit.area')"
        :hint="$t('initiative.edit.areaHint')"
        required
      ></v-select>
      <v-row>
        <v-col cols="4">
          <v-dialog width="auto">
            <template v-slot:activator="{ props: activatorProps }">
              <v-label>Start</v-label>
              <v-btn
                v-bind="activatorProps"
                class="mt-2"
                type="button"
                block
                flat
                outline
                color="info"
                >{{
                  formatDate(eventItem.startDate || new Date().toISOString())
                }}</v-btn
              >
            </template>
            <template v-slot:default="{ isActive }">
              <v-card>
                <v-card-text>
                  <v-row>
                    <v-col>
                      <v-date-picker
                        v-model="startDate"
                        @update:modelValue="updateStartDate"
                        label="Date"
                      ></v-date-picker
                    ></v-col>
                    <v-col>
                      <v-time-picker
                        v-model="startTime"
                        format="24hr"
                        scrollable
                        @update:modelValue="updateStartTime"
                        label="Time"
                      ></v-time-picker
                    ></v-col>
                  </v-row>

                  <v-btn color="info" @click="isActive.value = false"
                    >Close</v-btn
                  >
                </v-card-text>
              </v-card>
            </template>
          </v-dialog>
        </v-col>
        <v-col cols="4">
          <v-dialog width="auto">
            <template v-slot:activator="{ props: activatorProps }">
              <v-label>End</v-label>
              <v-btn
                v-bind="activatorProps"
                class="mt-2"
                type="button"
                block
                flat
                outline
                color="info"
                >{{
                  formatDate(eventItem.endDate || new Date().toISOString())
                }}</v-btn
              >
            </template>
            <template v-slot:default="{}">
              <v-card>
                <v-card-text>
                  <v-row>
                    <v-col>
                      <v-date-picker
                        v-model="endDate"
                        @update:modelValue="updateEndDate"
                        label="Date"
                      ></v-date-picker
                    ></v-col>
                    <v-col>
                      <v-time-picker
                        v-model="endTime"
                        format="24hr"
                        @update:modelValue="updateEndTime"
                        scrollable
                        label="Time"
                      ></v-time-picker
                    ></v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </template>
          </v-dialog>
        </v-col>
        <v-col cols="4">
          <v-select
            v-model="eventItem.repeats"
            label="Repeats"
            :items="repeatOptions"
            required
          ></v-select>
        </v-col>
      </v-row>

      <RichTextField
        label="Description"
        :height="200"
        :rules="descriptionRules"
        v-model="eventItem.description"
        :media="[]"
      />

      <!-- <CategoriesField v-model="eventItem.categories" /> -->

      <v-row>
        <v-col>
          <v-btn
            class="mt-2"
            type="button"
            color="warning"
            @click="removeLabel"
            block
            >Delete</v-btn
          >
        </v-col>
        <v-col>
          <v-btn class="mt-2" type="reset" block>Reset</v-btn>
        </v-col>
        <v-col>
          <v-btn class="mt-2" type="button" @click="save" color="success" block
            >{{ action }} <span v-if="isDirty">*</span></v-btn
          >
        </v-col>
        <v-col>
          <v-btn class="mt-2" type="submit" color="success" block
            >{{ action }} and close <span v-if="isDirty">*</span></v-btn
          ></v-col
        >
      </v-row>
    </v-container>
  </v-form>
</template>
<style scoped>
pre {
  font-family: sans-serif;
  font-size: 12px;
  background-color: rgb(255, 230, 171);
  display: block;
  height: 100%;
  padding: 8px;
}
</style>
